<template>
  <Component :is="withButton ? 'form' : 'div'" @submit.prevent="handleNextAction"
             class="space-y-4 max-w-sm mx-auto text-slate-900">
    <AuthTwoFactorChallenge v-if="show2fa"
                            @update:loading="(to) => emits('update:loading', to)"
                            is-not-form
                            ref="auth2fa"
                            :custom-success-handler="() => emits('logged-in')"/>

    <template v-else>
      <FormInput v-model="form.email" id="email" name="email" type="email" autocomplete="email" required
                 :autofocus="!props.focusPassword"
                 inputmode="email" label="Email"/>

      <FormInput v-model="form.password" id="password" name="password" :type="showPassword ? 'text' : 'password'"
                 autocomplete="current-password" :autofocus="!!props.focusPassword"
                 required
                 label="Password"/>

      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <input v-model="form.rememberMe" id="remember-me" name="remember-me" type="checkbox"
                 class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"/>
          <label for="remember-me" class="ml-2 block text-sm text-gray-900"> Remember me </label>
        </div>

        <div class="text-sm">
          <a href="/forgot-password"
                      class="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline">
            Forgot your password?
          </a>
        </div>
      </div>
    </template>
    <FormButton v-if="withButton" type="submit" size="full"
                :color="hexBackground || hexForeground ? 'hex' : 'gradient'"
                class="mt-4 lg:mt-8 block" bold
                :loading="loading"
                :hex-background="hexBackground"
                :hex-foreground="hexForeground">
      {{show2fa ? 'Confirm' : loginText}}
    </FormButton>

  </Component>
</template>

<script setup>
import {useAccountStore} from "../stores/AccountStore";
import {onMounted, reactive, ref, watch} from "vue";
import {checkIfEmailExists} from "../helpers/CommonAccountFunctions.js";
import {getItem, setItem} from "../helpers/LocalStorage.js";
import {useLocalStorage} from "@vueuse/core";
import FormInput from "./FormInput.vue";
import AuthTwoFactorChallenge from "../views/AuthTwoFactorChallenge.vue";
import FormButton from "./FormButton.vue";

const accountStore = useAccountStore();
const showPassword = ref(false);
const show2fa = ref(false);
const auth2fa = ref();

const emits = defineEmits(["logged-in", "requires-2fa", "update:loading"]);
const props = defineProps({
  focusPassword: Boolean,
  initialEmail: String,
  loading: Boolean,
  withButton: Boolean,
  hexBackground: String,
  hexForeground: String,
  loginText: {
    type: String,
    default: "Sign In"
  }
});

const form = reactive({
  email: "",
  password: "",
  rememberMe: false,
  emailExists: true,
});

const clearNewAccountLocalStorage = () => {
  const sessionUuid = useLocalStorage("session_uuid", null);
  sessionUuid.value = null;

  const newUser = useLocalStorage("new-user", null);
  newUser.value = null;

  const candidateProfileRequired = useLocalStorage("candidate-profile-required", null);
  candidateProfileRequired.value = null;
};

const handleNextAction = () => {
  if (show2fa.value) confirm2fa();
  else login();
};

const login = () => {
  clearNewAccountLocalStorage();

  if (accountStore.isLoggedIn) {
    emits("logged-in", true);
    return;
  }

  if (props.loading) return;
  window.progressBar.start();
  emits("update:loading", true);
  accountStore.login(form.email, form.password, form.rememberMe, getItem("setCompanyId"))
    .then(response => {
      if (response?.data?.id) {
        emits("logged-in");
        return;
      }

      show2fa.value = true;
      emits("requires-2fa");
    })
    .catch(async (e) => {
      console.error(e);
      form.emailExists = await checkIfEmailExists(form.email);
    })
    .finally(() => {
      window.progressBar.complete();
      emits("update:loading", false);
    });
};

const cancel2fa  = () => show2fa.value = false;
const confirm2fa = () => auth2fa.value.submitChallenge();

watch(() => props.initialEmail, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) form.email = newVal;
});

onMounted(() => {
  form.email = getItem("rememberEmail") || "";
  form.rememberMe = !!getItem("rememberEmail");
  const email = new URLSearchParams(window.location.search).get("email");
  const destination = new URLSearchParams(window.location.search).get("r");
  if (destination) setItem("targetUrl", destination);
  form.email = email ? decodeURIComponent(email) : props.initialEmail;
});

defineExpose({
  login,
  cancel2fa,
  confirm2fa,
});
</script>
